import { Utils } from 'klayowebshared';
import { DataContext } from '../../types/data.types';
import { SettingsEmployee } from './SettingsEmployee';
import { SettingsGroupMemberCount } from './SettingsGroupMemberCount';
import { SettingsGroupMemberList } from './SettingsGroupMemberList';

export interface MemberSelectionTypeOption {
  index: number;
  name: string;
  translationKey: string;
  value: string;
}

export interface MemberSelectionTypes {
  manual: MemberSelectionTypeOption;
  ruleBased: MemberSelectionTypeOption;
}

export interface GroupEntity {
  objectId: string;
  objectName: string;
  objectType: 'Employee' | 'JobDefinition' | 'Location';
  objectExtraInfo1?: string;
  objectIsDeleted?: boolean;
  /** Added by the organization's directory (a mapped department): can't be removed here. */
  fromDirectory?: boolean;
}

export interface GroupManager {
  employee?: {
    employeeId: string;
    fullName?: string;
    jobTitle?: string;
  };
  employeeId?: string;
  groupLevelType?: number;
  groupNotificationType?: number;
  isInstructor?: boolean;
}

export interface SettingsGroupData {
  groupId?: string;
  name?: string;
  description?: string;
  numberOfEmployees?: number;
  numberOfManagers?: number;
  memberSelectionType?: MemberSelectionTypeOption;
  isManualMemberSelection?: boolean;
  groupEntities?: GroupEntity[];
  managers?: GroupManager[];
  isLinkedToLearnUpon?: boolean;
  /** False: members the directory added are removed only in the directory. */
  canOverrideDirectory?: boolean;
}

export interface SettingsGroupApiDto {
  groupId: string | undefined;
  activeEmployeeId: string | null;
  name: string | undefined;
  description: string | undefined;
  employeeIds: string[] | null;
  jobDefinitionIds: string[] | null;
  locationIds: string[] | null;
  includeAllJobDefinitions: boolean;
  includeAllLocations: boolean;
  managers: {
    employeeId: string | undefined;
    levelTypeId: number | undefined;
    notificationTypeId: number | undefined;
    isInstructor: boolean | undefined;
  }[];
}

export class SettingsGroup {
  static memberSelectionType: MemberSelectionTypes = {
    manual: {
      index: 0,
      name: 'Manual',
      translationKey: 'addEditGroup.manual',
      value: 'manual',
    },
    ruleBased: {
      index: 1,
      name: 'Rule based',
      translationKey: 'addEditGroup.ruleBased',
      value: 'rule-based',
    },
  };

  id: string | undefined;
  groupId: string | undefined;
  name: string | undefined;
  description: string | undefined;
  numberOfEmployees: number | undefined;
  numberOfManagers: number | undefined;
  memberSelectionType: MemberSelectionTypeOption;
  members: SettingsEmployee[];
  managers: SettingsEmployee[];
  jobDefinitionIds: string[];
  locationIds: string[];
  ruleBasedMembersCount: SettingsGroupMemberCount | Record<string, never>;
  ruleBasedMembers: SettingsGroupMemberList | Record<string, never>;
  includeAllJobDefinitions: boolean | undefined;
  includeAllLocations: boolean | undefined;
  activeEmployeeId: string | null;
  isLinkedToLearnUpon: boolean | undefined;
  canOverrideDirectory = true;

  [key: string]: any;

  constructor(data?: SettingsGroupData | null) {
    this.members = [];
    this.managers = [];
    this.jobDefinitionIds = [];
    this.locationIds = [];
    this.ruleBasedMembersCount = {};
    this.ruleBasedMembers = {};

    if (data) {
      this.id = data.groupId;
      this.groupId = data.groupId;
      this.name = data.name;
      this.description = data.description;
      this.numberOfEmployees = data.numberOfEmployees;
      this.numberOfManagers = data.numberOfManagers;
      this.isLinkedToLearnUpon = data.isLinkedToLearnUpon;
      this.canOverrideDirectory = data.canOverrideDirectory ?? true;
      this.memberSelectionType = data.memberSelectionType
        ? data.memberSelectionType
        : data.isManualMemberSelection
          ? SettingsGroup.memberSelectionType.manual
          : SettingsGroup.memberSelectionType.ruleBased;
      if (data.groupEntities) {
        if (
          this.memberSelectionType ===
          SettingsGroup.memberSelectionType
            .manual /*data.groupEntities.some(e => e.objectType === 'Employee')*/
        ) {
          //this.memberSelectionType = SettingsGroup.memberSelectionType.manual;
          data.groupEntities.forEach((e: GroupEntity) =>
            this.members.push(
              new SettingsEmployee({
                employeeId: e.objectId,
                fullName: e.objectName,
                jobTitle: e.objectExtraInfo1,
                fromDirectory: e.fromDirectory,
              })
            )
          );
        } else {
          //this.memberSelectionType = SettingsGroup.memberSelectionType.ruleBased;
          this.jobDefinitionIds = data.groupEntities
            .filter((e: GroupEntity) => e.objectType === 'JobDefinition' && !e.objectIsDeleted)
            .map((e: GroupEntity) => e.objectId);
          this.locationIds = data.groupEntities
            .filter((e: GroupEntity) => e.objectType === 'Location' && !e.objectIsDeleted)
            .map((e: GroupEntity) => e.objectId);

          this.includeAllJobDefinitions = !data.groupEntities.some(
            (e: GroupEntity) => e.objectType === 'JobDefinition'
          );
          this.includeAllLocations = !data.groupEntities.some(
            (e: GroupEntity) => e.objectType === 'Location'
          );
        }
      }

      if (data.managers)
        data.managers.forEach((m: GroupManager) => {
          this.managers.push(new SettingsEmployee({ ...m.employee, ...m }));
        });
    } else {
      this.memberSelectionType = SettingsGroup.memberSelectionType.manual;
    }

    this.activeEmployeeId = null;
  }

  addMember(employee: SettingsEmployee): void {
    if (this.members.some((m: SettingsEmployee) => m.employeeId === employee.employeeId)) return;
    this.members = [...this.members];
    this.members.push(employee);
    this.numberOfEmployees = this.members.length;
  }

  removeMember(employee: SettingsEmployee): void {
    const members = [...this.members];
    const index = members.indexOf(employee);
    if (index !== -1) members.splice(index, 1);
    this.members = [...members];
    this.numberOfEmployees = this.members.length;
  }

  addManager(employee: SettingsEmployee): void {
    if (this.managers.some((m: SettingsEmployee) => m.employeeId === employee.employeeId)) return;
    this.managers = [...this.managers];
    this.managers.push(employee);
    this.numberOfManagers = this.managers.length;
  }

  removeManager(employee: SettingsEmployee): void {
    const managers = [...this.managers];
    const index = managers.indexOf(employee);
    if (index !== -1) managers.splice(index, 1);
    this.managers = [...managers];
    this.numberOfManagers = this.managers.length;
  }

  isManualSelection(): boolean {
    return this.memberSelectionType === SettingsGroup.memberSelectionType.manual;
  }

  isRuleBasedSelection(): boolean {
    return this.memberSelectionType === SettingsGroup.memberSelectionType.ruleBased;
  }

  compareMemberSelection(other: SettingsGroup): boolean {
    if (this.memberSelectionType !== other.memberSelectionType) return false;
    if (this.isManualSelection()) return this.compareMembers(other);
    else {
      if (!(this.includeAllJobDefinitions === other.includeAllJobDefinitions)) return false;
      if (this.includeAllLocations !== other.includeAllLocations) return false;
      if (
        !Utils.compareList(
          this.jobDefinitionIds,
          other.jobDefinitionIds,
          (j1: string, j2: string) => j1 === j2
        )
      )
        return false;
      if (
        !Utils.compareList(
          this.locationIds,
          other.locationIds,
          (l1: string, l2: string) => l1 === l2
        )
      )
        return false;
      return true;
    }
  }

  compareMembers(other: SettingsGroup): boolean {
    return Utils.compareList(
      this.members,
      other.members,
      (m1: SettingsEmployee, m2: SettingsEmployee) => m1.employeeId === m2.employeeId
    );
  }

  compareManagers(other: SettingsGroup): boolean {
    return Utils.compareList(
      this.managers,
      other.managers,
      (m1: SettingsEmployee, m2: SettingsEmployee) =>
        m1.employeeId === m2.employeeId &&
        m1.groupLevelType === m2.groupLevelType &&
        m1.groupNotificationType === m2.groupNotificationType &&
        m1.isInstructor === m2.isInstructor &&
        m1.isTrainingManager === m2.isTrainingManager &&
        m1.trainingManagerConfigurationTypeId === m2.trainingManagerConfigurationTypeId
    );
  }

  loadRuleBasedMembers(
    context: DataContext,
    updateCache?: boolean
  ): Promise<SettingsGroupMemberList> {
    return new Promise((resolve, reject) => {
      if (this.ruleBasedMembers instanceof SettingsGroupMemberList && updateCache !== true) {
        resolve(this.ruleBasedMembers);
      } else {
        this.ruleBasedMembers = new SettingsGroupMemberList({
          // params: this.getRuleBasedUrlParams(),
          resolve,
          reject,
          context,
          postData: {
            jobDefinitionIds: this.jobDefinitionIds,
            locationIds: this.locationIds,
            managerIds: this.managers.map((m: SettingsEmployee) => m.employeeId),
          },
        });
        this.numberOfEmployees = this.ruleBasedMembers.employees.length;
      }
    });
  }

  loadRuleBasedMembersCount(
    context: DataContext,
    updateCache?: boolean
  ): Promise<SettingsGroupMemberCount> {
    return new Promise((resolve, reject) => {
      if (this.ruleBasedMembersCount instanceof SettingsGroupMemberCount && updateCache !== true) {
        resolve(this.ruleBasedMembersCount);
      } else {
        this.ruleBasedMembersCount = new SettingsGroupMemberCount({
          // params: this.getRuleBasedUrlParams(),
          resolve,
          reject,
          context,
          postData: {
            jobDefinitionIds: this.jobDefinitionIds,
            locationIds: this.locationIds,
            managerIds: this.managers.map((m: SettingsEmployee) => m.employeeId),
          },
        });
        this.numberOfEmployees = this.ruleBasedMembersCount.totalEmployees;
      }
    });
  }

  getRuleBasedUrlParams(): string {
    const params = new URLSearchParams();

    this.jobDefinitionIds.forEach((id: string) => params.append('JobDefinitionIds', id));
    this.locationIds.forEach((id: string) => params.append('LocationIds', id));
    this.managers.forEach((m: SettingsEmployee) => {
      if (m.employeeId) params.append('ManagerIds', m.employeeId);
    });

    return params.toString();
  }

  toApiDto(): SettingsGroupApiDto {
    return {
      groupId: this.groupId,
      activeEmployeeId: this.activeEmployeeId,
      name: this.name,
      description: this.description,
      employeeIds: this.isManualSelection()
        ? (this.members.map((e: SettingsEmployee) => e.employeeId).filter(Boolean) as string[])
        : null,
      jobDefinitionIds:
        this.isRuleBasedSelection() && this.jobDefinitionIds.length > 0
          ? this.jobDefinitionIds
          : null,
      locationIds:
        this.isRuleBasedSelection() && this.locationIds.length > 0 ? this.locationIds : null,
      includeAllJobDefinitions: this.includeAllJobDefinitions === true,
      includeAllLocations: this.includeAllLocations === true,
      managers: this.managers.map((m: SettingsEmployee) => {
        return {
          employeeId: m.employeeId,
          levelTypeId: m.groupLevelType,
          notificationTypeId: m.groupNotificationType,
          isInstructor: m.isInstructor,
          trainingManagerConfigurationTypeId: m.trainingManagerConfigurationTypeId,
        };
      }),
    };
  }
}
